import { useState } from "react";
import Card from "../UI/Card";
import IconButton from "../UI/IconButton";
import Button from "../UI/Button";
import styles from "./TenantProfileCard.module.css";
import moment from "moment/moment";
import { Link, useLocation, useNavigate } from "react-router-dom"; // @ts-expect-error pure js
import TenantShowFiles from "../Tenant/TenantShowFiles";
import Avatar from "../UI/Avatar";
import Text from "../UI/Text"; // @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx"; // @ts-expect-error pure js
import DisplayName from "../UI/DisplayName"; // @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions"; // @ts-expect-error pure js
import { classnames, TextOneOrMany } from "../../utils"; // @ts-expect-error pure js
import { ProfileInviteStatus } from "../../models/profile";
import InviteLocataireModal from "./InviteLocataireModal";
import RevokeInviteDialog from "./RevokeInviteDialog";
import {
  IconChevronDown,
  IconCornerDownRight,
  IconDotsVertical,
  IconFileText,
  IconMailForward,
  IconPencil,
  IconPlus,
  IconTrash,
  IconUserOff,
} from "@tabler/icons-react";
import DropdownMenu from "../UI/DropdownMenu";
import FieldRow from "./FieldRow";
import ProfileBadge from "./ProfileBadge";
import type { Profile } from "./types";
import { getAddress, getInitials } from "./profileUtils";
import ProfileFieldsGroup from "./ProfileFieldsGroup.tsx";

interface TenantProfileCardProps {
  modifierAction?: ((profileId: number) => void) | null | false;
  addGarantAction?: ((profileId: number) => void) | null | false;
  addRepresentantAction?: ((profileId: number) => void) | null | false;
  supprimerAction?: (() => void) | null | false;
  archiverAction?: (() => void) | null | false;
  activateAction?: (() => void) | null | false;
  inviteAction?: (() => void | Promise<void>) | null | false;
  revokeInviteAction?: (() => void | Promise<void>) | null | false;
  profile?: Profile;
  profilePrimaryKey?: string;
  isGarant?: boolean;
  isRepresentant?: boolean;
  removeGarant?: ((profileId: number) => void) | null | false;
  removeRepresentant?: ((profileId: number) => void) | null | false;
  minimalDisplay?: boolean;
  dontAddGarant?: boolean;
}

export default function TenantProfileCard({
  modifierAction,
  addGarantAction,
  addRepresentantAction,
  supprimerAction,
  archiverAction,
  activateAction,
  inviteAction,
  revokeInviteAction,
  profile: initialProfile,
  profilePrimaryKey = "id",
  isGarant,
  isRepresentant,
  removeGarant,
  removeRepresentant,
  minimalDisplay,
  dontAddGarant,
}: TenantProfileCardProps) {
  const [Modal, , openModal] = useModal(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const profile = initialProfile;
  if (!profile) return null;

  const isInvited = profile.status_invite === ProfileInviteStatus.INVITED;
  const isConnected = profile.status_invite === ProfileInviteStatus.CONNECTED;

  const initials = getInitials(profile);
  const address = getAddress(profile);

  const birthInfo = profile.birthday
    ? `Né le ${moment(profile.birthday, "YYYY-MM-DD").format(
        "DD MMMM YYYY",
      )} (${moment().diff(profile.birthday, "years")} ans)${
        profile.lieu_naissance ? " à " + profile.lieu_naissance : ""
      }`
    : null;

  const shortBirthInfo = profile.birthday
    ? `Né le ${moment(profile.birthday, "YYYY-MM-DD").format("DD MMMM YYYY")}`
    : null;

  const isSubProfile = isGarant || isRepresentant;

  const handleEdit = () =>
    modifierAction && modifierAction(profile[profilePrimaryKey] as number);
  const handleAddGarant = () =>
    addGarantAction
      ? addGarantAction(profile[profilePrimaryKey] as number)
      : navigate(`${profile.id}/add_garant${location.search}`);
  const handleAddRepresentant = () =>
    addRepresentantAction &&
    addRepresentantAction(profile[profilePrimaryKey] as number);

  if (minimalDisplay) {
    const showAddGarant = !isSubProfile && !dontAddGarant;
    const showAddRepresentant =
      !isSubProfile && !dontAddGarant && !!addRepresentantAction;
    const avatarVariant = isGarant
      ? "orange"
      : isRepresentant
      ? "purple"
      : undefined;

    return (
      <div className={styles.minimalGroup}>
        <Modal>
          <TenantShowFiles files={profile.files} />
        </Modal>

        <Card
          padding="xs"
          className={classnames(
            styles.minimalCard,
            isGarant && styles.minimalCardGarant,
          )}
        >
          <div className={styles.minimalHeader}>
            <div className={styles.minimalIdentity}>
              <Avatar initials={initials} size="md" variant={avatarVariant} />
              <div className={styles.minimalInfo}>
                <div className={styles.minimalNameRow}>
                  <Text weight="bold">
                    <DisplayName profile={profile} />
                  </Text>
                  {isGarant && (
                    <ProfileBadge variant="orange">Garant</ProfileBadge>
                  )}
                  {isRepresentant && (
                    <ProfileBadge variant="violet">
                      Représentant légal
                    </ProfileBadge>
                  )}
                </div>
                {shortBirthInfo && (
                  <Text variant="deci" color="muted">
                    {shortBirthInfo}
                  </Text>
                )}
              </div>
            </div>
            <div className={styles.minimalActions}>
              {showAddRepresentant && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddRepresentant}
                  icon={IconPlus}
                  className={styles.minimalAddGarantButton}
                >
                  Ajouter un représentant légal
                </Button>
              )}
              {showAddGarant && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleAddGarant}
                  icon={IconPlus}
                  className={styles.minimalAddGarantButton}
                >
                  Ajouter un garant
                </Button>
              )}
              {isGarant && removeGarant && (
                <IconButton
                  onClick={() =>
                    removeGarant(profile[profilePrimaryKey] as number)
                  }
                  label="Supprimer le garant"
                  icon={IconTrash}
                  variant="default"
                />
              )}
              {isRepresentant && removeRepresentant && (
                <IconButton
                  onClick={() =>
                    removeRepresentant(profile[profilePrimaryKey] as number)
                  }
                  label="Supprimer le représentant légal"
                  icon={IconTrash}
                  variant="default"
                />
              )}
              {!isSubProfile && supprimerAction && (
                <IconButton
                  onClick={supprimerAction}
                  label="Supprimer"
                  icon={IconTrash}
                  variant="default"
                />
              )}
              {modifierAction && (
                <IconButton
                  onClick={handleEdit}
                  label="Modifier"
                  icon={IconPencil}
                  variant={isSubProfile ? "primary" : "default"}
                />
              )}
            </div>
          </div>
        </Card>

        {!isSubProfile &&
          profile.legal_representatives &&
          profile.legal_representatives.length > 0 &&
          profile.legal_representatives.map((representant, i) => (
            <div key={i} className={styles.garantRow}>
              <IconCornerDownRight />
              <div>
                <TenantProfileCard
                  profile={representant}
                  isRepresentant
                  minimalDisplay
                  removeRepresentant={removeRepresentant}
                  modifierAction={modifierAction}
                  profilePrimaryKey={profilePrimaryKey}
                />
              </div>
            </div>
          ))}

        {!isSubProfile &&
          profile.garantors &&
          profile.garantors.length > 0 &&
          profile.garantors.map((garantor, i) => (
            <div key={i} className={styles.garantRow}>
              <IconCornerDownRight />
              <div>
                <TenantProfileCard
                  profile={garantor}
                  isGarant
                  minimalDisplay
                  removeGarant={removeGarant}
                  modifierAction={modifierAction}
                  profilePrimaryKey={profilePrimaryKey}
                />
              </div>
            </div>
          ))}
      </div>
    );
  }

  return (
    <Card padding="xs">
      {profile && (
        <>
          <Modal>
            <TenantShowFiles files={profile.files} />
          </Modal>
          {inviteAction && (
            <InviteLocataireModal
              open={isInviteOpen}
              onOpenChange={setIsInviteOpen}
              profiles={[profile]}
              onInvite={() => inviteAction()}
              resend={isInvited}
            />
          )}
          {revokeInviteAction && (
            <RevokeInviteDialog
              open={isRevokeConfirmOpen}
              onOpenChange={setIsRevokeConfirmOpen}
              isConnected={isConnected}
              onRevoke={revokeInviteAction}
            />
          )}

          <div
            className={classnames(
              styles.header,
              isGarant && styles.headerGarant,
              styles.headerExpanded,
            )}
          >
            <div className={styles.avatarWrapper}>
              <Avatar initials={initials} size="md" />
            </div>
            <div
              className={classnames(
                styles.headerButtons,
                styles.headerButtonsExpanded,
              )}
            >
              {modifierAction && (
                <IconButton
                  onClick={() =>
                    modifierAction(profile[profilePrimaryKey] as number)
                  }
                  label="Modifier"
                  icon={IconPencil}
                  requiresWrite
                />
              )}
              <IconButton
                onClick={openModal}
                label="Documents"
                icon={IconFileText}
              />
              {isGarant && removeGarant && (
                <IconButton
                  onClick={() =>
                    removeGarant(profile[profilePrimaryKey] as number)
                  }
                  label="Supprimer"
                  icon={IconTrash}
                  requiresWrite
                />
              )}
              {isRepresentant && removeRepresentant && (
                <IconButton
                  onClick={() =>
                    removeRepresentant(profile[profilePrimaryKey] as number)
                  }
                  label="Supprimer le représentant légal"
                  icon={IconTrash}
                  requiresWrite
                />
              )}
              {(activateAction ||
                archiverAction ||
                supprimerAction ||
                (!isSubProfile && !dontAddGarant)) && (
                <DropdownMenu icon={IconDotsVertical} label="Plus d'options">
                  {activateAction && !isSubProfile && (
                    <DropdownMenu.Item
                      label="Réactiver"
                      onClick={activateAction}
                      requiresWrite
                    />
                  )}
                  {archiverAction && !isSubProfile && (
                    <DropdownMenu.Item
                      label="Archiver"
                      onClick={archiverAction}
                      requiresWrite
                    />
                  )}
                  {supprimerAction && !isSubProfile && (
                    <DropdownMenu.Item
                      label="Supprimer"
                      onClick={supprimerAction}
                      requiresWrite
                    />
                  )}
                  {!isSubProfile && !dontAddGarant && (
                    <DropdownMenu.Item
                      label="Ajouter un représentant légal"
                      onClick={
                        addRepresentantAction
                          ? () =>
                              addRepresentantAction(
                                profile[profilePrimaryKey] as number,
                              )
                          : () =>
                              navigate(
                                `${profile.id}/add_representant${location.search}`,
                              )
                      }
                      requiresWrite
                    />
                  )}
                  {!isSubProfile && !dontAddGarant && (
                    <DropdownMenu.Item
                      label="Ajouter un garant"
                      onClick={
                        addGarantAction
                          ? () =>
                              addGarantAction(
                                profile[profilePrimaryKey] as number,
                              )
                          : () =>
                              navigate(
                                `${profile.id}/add_garant${location.search}`,
                              )
                      }
                      requiresWrite
                    />
                  )}
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className={styles.body}>
            <div className={styles.titleRow}>
              <div className={styles.nameBlock}>
                <Text variant="base" weight="bold">
                  <DisplayName profile={profile} />
                </Text>
                {parseInt(profile.type as string) === 1 && birthInfo && (
                  <Text variant="deci" color="muted">
                    {birthInfo}
                  </Text>
                )}
                {parseInt(profile.type as string) === 2 && (
                  <Text variant="deci" color="muted">
                    Représentée par {profile.prenom} {profile.nom}
                  </Text>
                )}
              </div>
              <div className={styles.titleActions}>
                {isInvited &&
                  (inviteAction || revokeInviteAction ? (
                    <DropdownMenu
                      trigger={
                        <ProfileBadge className={styles.badgeTrigger}>
                          Invité <IconChevronDown size={12} />
                        </ProfileBadge>
                      }
                      nativeButton={false}
                      align="end"
                    >
                      {inviteAction && (
                        <DropdownMenu.Item
                          label="Renvoyer l'invitation"
                          icon={IconMailForward}
                          onClick={() => setIsInviteOpen(true)}
                          requiresWrite
                        />
                      )}
                      {revokeInviteAction && (
                        <DropdownMenu.Item
                          label="Révoquer l'invitation"
                          icon={IconUserOff}
                          variant="danger"
                          onClick={() => setIsRevokeConfirmOpen(true)}
                          requiresWrite
                        />
                      )}
                    </DropdownMenu>
                  ) : (
                    <ProfileBadge>Invité</ProfileBadge>
                  ))}
                {isConnected &&
                  (revokeInviteAction ? (
                    <DropdownMenu
                      trigger={
                        <ProfileBadge
                          variant="success"
                          className={styles.badgeTrigger}
                        >
                          Connecté <IconChevronDown size={12} />
                        </ProfileBadge>
                      }
                      nativeButton={false}
                      align="end"
                    >
                      <DropdownMenu.Item
                        label="Révoquer l'accès"
                        icon={IconUserOff}
                        variant="danger"
                        onClick={() => setIsRevokeConfirmOpen(true)}
                        requiresWrite
                      />
                    </DropdownMenu>
                  ) : (
                    <ProfileBadge variant="success">Connecté</ProfileBadge>
                  ))}
                {inviteAction && !isInvited && !isConnected && (
                  <Button
                    onClick={() => setIsInviteOpen(true)}
                    size="sm"
                    requiresWrite
                  >
                    Inviter
                  </Button>
                )}
              </div>
            </div>

            <ProfileFieldsGroup className={styles.fields}>
              <FieldRow label="Numéro de téléphone" value={profile.tel} />
              <FieldRow label="Adresse email" value={profile.mail} />
              <FieldRow label="Adresse" value={address} />
              <>
                <FieldRow
                  label="Activité professionnelle"
                  value={profile.activite}
                />
                {parseInt(profile.type as string) === 1 &&
                  profile.revenus_brut && (
                    <FieldRow
                      label="Revenus"
                      value={currencyFormatter(profile.revenus_brut)}
                    />
                  )}
              </>
            </ProfileFieldsGroup>

            {profile.caution_type === "Visale" && (
              <div className={styles.garantSection}>
                <Text
                  variant="deci"
                  weight="bold"
                  className={styles.garantTitle}
                >
                  Garant
                </Text>
                <div className={styles.garantCard}>
                  <ProfileFieldsGroup>
                    <Link to={"https://www.visale.fr"} target="_blank">
                      <img src={"/logo-visale.png"} alt={"Logo Visale"} />
                    </Link>
                    <FieldRow
                      label="Numéro du visa"
                      value={profile.contract_number || "Non renseigné"}
                    />
                    <FieldRow
                      label="Loyer maximum garanti"
                      value={
                        currencyFormatter(profile.loyer_max_visale) ||
                        "Non renseigné"
                      }
                    />
                    <FieldRow
                      label="Date de validité du visa"
                      value={
                        moment(profile.validite_visale).format(
                          "DD MMMM YYYY",
                        ) || "Non renseigné"
                      }
                    />
                  </ProfileFieldsGroup>
                </div>
              </div>
            )}

            {profile.caution_type !== "Visale" &&
              profile.garantors &&
              profile.garantors.length > 0 && (
                <div className={styles.garantSection}>
                  <Text
                    variant="deci"
                    weight="bold"
                    className={styles.garantTitle}
                  >
                    <TextOneOrMany
                      one={"Garant associé"}
                      many={"Garants associés"}
                      number={profile.garantors.length}
                      none={""}
                    />
                  </Text>
                  <div className={styles.garantGrid}>
                    {profile.garantors.map((garantor, i) => (
                      <TenantProfileCard
                        profile={garantor}
                        key={i}
                        isGarant
                        removeGarant={removeGarant}
                        modifierAction={modifierAction}
                        profilePrimaryKey={profilePrimaryKey}
                        minimalDisplay={minimalDisplay}
                      />
                    ))}
                  </div>
                </div>
              )}

            {profile.legal_representatives &&
              profile.legal_representatives.length > 0 && (
                <div className={styles.garantSection}>
                  <Text
                    variant="deci"
                    weight="bold"
                    className={styles.garantTitle}
                  >
                    <TextOneOrMany
                      one={"Représentant légal"}
                      many={"Représentants légaux"}
                      number={profile.legal_representatives.length}
                      none={""}
                    />
                  </Text>
                  <div className={styles.garantGrid}>
                    {profile.legal_representatives.map((representant, i) => (
                      <TenantProfileCard
                        profile={representant}
                        key={i}
                        isRepresentant
                        removeRepresentant={removeRepresentant}
                        modifierAction={modifierAction}
                        profilePrimaryKey={profilePrimaryKey}
                        minimalDisplay={minimalDisplay}
                      />
                    ))}
                  </div>
                </div>
              )}
          </div>
        </>
      )}
    </Card>
  );
}
